import React, { useEffect, useMemo, useRef } from 'react';
import './Ridgeline.css';

// Small seeded PRNG so the scene is identical on every load.
const mulberry32 = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const WIDTH = 1440;
const HEIGHT = 480;

// Build a ridge as a closed path filling down to the bottom edge.
// Straight segments with a random walk read as rock, not waves.
const ridgePath = (rand, { baseline, amplitude, segments, jaggedness }) => {
  const step = WIDTH / segments;
  const points = [];
  let y = baseline;
  for (let i = 0; i <= segments; i += 1) {
    const x = i * step;
    // Drift, pulled gently back toward the baseline so it never wanders off.
    y += (rand() - 0.5) * amplitude * 0.9 + (baseline - y) * 0.25;
    const peak = rand() < jaggedness ? rand() * amplitude * 0.8 : 0;
    points.push([Math.round(x), Math.round(y - peak)]);
  }
  const line = points.map(([x, py]) => `L${x} ${py}`).join(' ');
  return `M0 ${HEIGHT} ${line} L${WIDTH} ${HEIGHT} Z`;
};

const buildScene = () => {
  const rand = mulberry32(20250904);
  const layers = [
    { baseline: 235, amplitude: 60, segments: 22, jaggedness: 0.3, fill: 'var(--ridge-far)', depth: 0.12 },
    { baseline: 315, amplitude: 48, segments: 30, jaggedness: 0.22, fill: 'var(--ridge-mid)', depth: 0.22 },
    { baseline: 395, amplitude: 34, segments: 40, jaggedness: 0.15, fill: 'var(--ridge-near)', depth: 0.34 },
  ].map((layer) => ({ ...layer, d: ridgePath(rand, layer) }));

  const stars = Array.from({ length: 90 }, () => ({
    x: rand() * WIDTH,
    y: rand() * 230,
    r: 0.5 + rand() * 1.1,
    delay: rand() * 6,
    twinkle: rand() < 0.3,
  }));

  return { layers, stars };
};

const Ridgeline = () => {
  const { layers, stars } = useMemo(buildScene, []);
  const layerRefs = useRef([]);

  // Gentle parallax: nearer ridges drift a touch more than farther ones.
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, 900);
        layerRefs.current.forEach((el, i) => {
          if (el) el.style.transform = `translateY(${y * layers[i].depth}px)`;
        });
        frame = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [layers]);

  return (
    <div className="ridgeline" aria-hidden="true">
      <svg
        className="ridgeline-svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMax slice"
      >
        <defs>
          <radialGradient id="ember-glow" cx="0.5" cy="0.85" r="0.6">
            <stop offset="0" stopColor="#e8873a" stopOpacity="0.8" />
            <stop offset="0.4" stopColor="#e8873a" stopOpacity="0.22" />
            <stop offset="1" stopColor="#e8873a" stopOpacity="0" />
          </radialGradient>
        </defs>

        <g className="stars">
          {stars.map((s, i) => (
            <circle
              key={i}
              cx={s.x}
              cy={s.y}
              r={s.r}
              className={s.twinkle ? 'star star-twinkle' : 'star'}
              style={s.twinkle ? { animationDelay: `${s.delay}s` } : undefined}
            />
          ))}
        </g>

        {layers.map((layer, i) => (
          <React.Fragment key={i}>
            {i === layers.length - 1 && (
              <ellipse
                className="ember"
                cx="430"
                cy="372"
                rx="280"
                ry="120"
                fill="url(#ember-glow)"
              />
            )}
            <path
              ref={(el) => { layerRefs.current[i] = el; }}
              d={layer.d}
              fill={layer.fill}
              className={`ridge ridge-${i}`}
            />
          </React.Fragment>
        ))}
      </svg>
    </div>
  );
};

export default Ridgeline;
